import Button, { ButtonType } from "components/Elements/Buttonv2";
import {
  BranchType,
  MultisplitBranch,
  MultisplitNodeData,
} from "pages/FlowBuilderv2/Nodes/NodeData";
import React, { FC, Suspense, useEffect, useState } from "react";
import { SidePanelComponentProps } from "../FlowBuilderSidePanel";
import {
  ConditionalSegmentsSettings,
  Query,
} from "reducers/flow-builder.reducer";
import { v4 as uuid } from "uuid";
import MultisplitCondtionsReview from "../components/MultisplitCondtionsReview";
import { useAppSelector } from "store/hooks";
import { useSidePanelReadOnly } from "../SidePanelReadOnlyContext";
const FlowBuilderMultisplitModal = React.lazy(
  () => import("../../Modals/FlowBuilderMultisplitModal")
);

export const limitQuery = (
  query: Query,
  limit: number,
  count = 0
): [Query, number] => {
  if (!limit || count >= limit) {
    return [query, count];
  }

  const limitedQuery: Query = { ...query, statements: [] };
  for (const statement of query.statements) {
    if (count >= limit) {
      break;
    }
    if ((statement as Query)?.isSubBuilderChild) {
      const [limitedStatement, statementCount] = limitQuery(
        statement as Query,
        limit,
        count
      );
      limitedQuery.statements.push(limitedStatement);
      count += statementCount;
    } else {
      limitedQuery.statements.push(statement);
      count++;
    }
  }

  return [limitedQuery, count];
};

const MultisplitSettings: FC<
  SidePanelComponentProps<MultisplitNodeData> & { isViewMode?: boolean }
> = ({ nodeData, setNodeData, setIsError, showErrors, isViewMode }) => {
  const readOnly = useSidePanelReadOnly();
  const [editBranchIndex, setEditBranchIndex] = useState<number | undefined>(
    undefined
  );
  const flowBuilderState = useAppSelector((state) => state.flowBuilder);

  const branchEdges = flowBuilderState.edges.filter(
    (edge) => edge.type === "branch"
  );
  const currentBranchEdge = branchEdges?.[branchEdges.length - 1]; // Get the last branch edge added

  const defaultOtherBranchObject: MultisplitBranch = {
    id: currentBranchEdge?.id || uuid(),
    type: BranchType.MULTISPLIT,
    isOthers: true,
  };

  const handleSave = (branch: ConditionalSegmentsSettings) => {
    if (editBranchIndex === undefined) return;

    const newData = { ...nodeData };
    if (editBranchIndex === -1) {
      const newBranch: MultisplitBranch = {
        id: uuid(),
        type: BranchType.MULTISPLIT,
        conditions: branch,
      };
      if (!nodeData.branches.find((b) => b.isOthers)) {
        newData.branches.unshift(newBranch);
      } else {
        newData.branches.push(newBranch);
      }
    } else {
      newData.branches[editBranchIndex].conditions = branch;
    }

    setNodeData(newData);
    setEditBranchIndex(undefined);
  };

  const handleDelete = (index: number) => {
    const newData = { ...nodeData };
    newData.branches.splice(index, 1);

    setNodeData(newData);
  };

  useEffect(() => {
    if (!nodeData || nodeData.branches.find((branch) => branch.isOthers)) {
      return;
    }

    const defaultData = {
      ...nodeData,
      branches: [...nodeData?.branches, defaultOtherBranchObject],
    };

    setNodeData(defaultData);
  }, [nodeData]);

  return (
    <div className={readOnly ? "pointer-events-none select-none" : undefined}>
      <div className="flex flex-col gap-[10px]">
        {nodeData.branches
          .filter((el) => el.conditions)
          .map((el, i) => {
            return (
              <React.Fragment key={el.id}>
                <div key={el.id} className="relative pb-[20px]">
                  <div className="w-full text-themed-text text-[14px] font-inter font-semibold mb-[10px]">
                    Branch {i + 1}
                  </div>
                  <MultisplitCondtionsReview
                    condition={limitQuery(el.conditions!.query, 3)[0]}
                  />
                  {limitQuery(el.conditions!.query, 3)[1] >= 3 && (
                    <div
                      className="cursor-pointer select-none text-themed-text mt-[10px] text-[14px] font-roboto w-full px-[15px] py-[4px] text-center border rounded border-themed-border"
                      onClick={() => setEditBranchIndex(i)}
                    >
                      See all conditions
                    </div>
                  )}
                  {!isViewMode && (
                    <>
                      <div className="flex gap-[10px] mt-[10px]">
                        <Button
                          type={ButtonType.LINK}
                          onClick={() => setEditBranchIndex(i)}
                          className="!text-[#6366F1]"
                        >
                          Edit branch
                        </Button>
                        <Button
                          type={ButtonType.LINK}
                          onClick={() => {
                            handleDelete(i);
                          }}
                          className="!text-[#EB5757]"
                        >
                          Delete branch
                        </Button>
                      </div>
                      <div className="w-[calc(100%+20px)] absolute left-[-20px] z-10 bottom-[0px] border-themed-border border-t-[1px]" />
                    </>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        {!!(
          nodeData.branches.length === 1 &&
          nodeData.branches.find((branch) => branch.isOthers)
        ) && (
          <>
            <div className="relative w-full text-themed-text text-[14px] font-inter font-semibold mb-[5px]">
              Branch - All others
            </div>
            <div className="relative w-full text-themed-textSecondary text-[12px] font-inter mb-[10px]">
              This branch is created by default. Please add conditions to other
              branches to avoid all contacts falling into this branch.
            </div>
          </>
        )}
      </div>
      {!isViewMode && (
        <div className="py-5 relative">
          <Button
            type={ButtonType.SECONDARY}
            onClick={() => {
              setEditBranchIndex(-1);
            }}
            className="!text-themed-text !border-themed-border"
          >
            Add branch
          </Button>
        </div>
      )}

      <Suspense fallback={<></>}>
        <FlowBuilderMultisplitModal
          isOpen={editBranchIndex !== undefined}
          branch={
            editBranchIndex === -1 || editBranchIndex === undefined
              ? undefined
              : nodeData.branches[editBranchIndex!].conditions
          }
          index={editBranchIndex}
          onSave={handleSave}
          onClose={() => setEditBranchIndex(undefined)}
          isViewMode={isViewMode}
        />
      </Suspense>
    </div>
  );
};

export default MultisplitSettings;
